import { ChangeDetectorRef, Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IAccountSettings } from '@app/core/interfaces/user/IAccountSettings';
import { UserService } from '@app/core/services/UserService';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { BankService } from '@app/core/services/BankService';
import { SwedishBankAccountValidationService } from '@app/core/services/SwedishBankAccountValidationService';
import { MunicipalityService } from '@app/core/services/municipalityService';
import { IUser } from '@app/core/interfaces/user/IUser';
import { ICountryType } from '@app/core/interfaces/ICountryType';
import { IBank } from '@app/core/interfaces/economy/IBank';
import { IFile } from '@app/core/interfaces/IFile';
import { FileService } from '@app/core/services/FileService';
import { IMunicipality } from '@app/core/interfaces/IMunicipality';
//import { ITaxAdjustment } from '@app/core/interfaces/user/ITaxAdjustment';
import { ITaxAdjustment } from '../../core/interfaces/user/ITaxAdjustment';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, ActivatedRoute } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatDatepicker, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { DataSource } from '@angular/cdk/collections';
import { CanComponentDeactivate } from '@app/core/guards/CanDeactivate.Component';

import {
  selectorSettings, 
  ActionSettingsChangeTheme,
  ActionSettingsChangeAutoNightMode,
  ActionSettingsChangeAnimationsPage,
  ActionSettingsChangeAnimationsElements,
  SettingsState,
  ActionSettingsPersist,
} from '../settings.reducer';
import { EditTaxAdjustmentDialogComponent } from './tax-adjustment/edit-tax-adjustment-Dialog.Component';

import {
  ActionAuthLogin,
  ActionAuthLogout,
  AnimationsService,
  selectorAuth,
  routeAnimations
} from '@app/core';
import { environment as env } from '@env/environment';
import { debug } from '@app/core/meta-reducers/debug.reducer';

@Component({
  standalone: false,
  selector: 'anms-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } }
  ]
})
export class SettingsComponent implements OnInit, OnDestroy  {
  private unsubscribe$: Subject<void> = new Subject<void>();
  private loadUserRequestId = 0;
  private bankValidationTimer: ReturnType<typeof setTimeout> | null = null;
  settings: SettingsState;
  public isAdmin: boolean = false;
  public isCompany: boolean = false;
  public dataSource: MatTableDataSource<ITaxAdjustment>;
  public displayedColumns
  public taxadjustmentsEdited: boolean = false;
  @ViewChild('f', { static: false }) form;
  validationErrorMessage: string;
  coordinationErrorMessage: string;
  emailErrorMessage: string = '';

  get displayedEmail(): string {
    const email = this.user?.Email || '';
    return email.trim().toLowerCase().endsWith('@tmpemail.com') ? '' : email;
  }

  bankValidationMessage: string = '';
  bankEntryMode: 'swedish' | 'personkonto' | 'international' = 'swedish';
  countries = [];
  registeredAbroad = false;
  public appId: number = env.appId;
  personalNumberPattern = "[0-9]{12}";
  selectedNotificationTypes = [];
  CustomServiceFee = 0;
  Commission = 0;
  PensionSettingTypeId = 1;
  public banks: IBank[];
  public isChildAccount: boolean = false;
  coordinationNumberValid: boolean = true;
  copied = false;
  referrals: any[] = [];
  payoutTypes = [{ id: 1, name: 'Löpande' }, { id: 2, name: 'Fast datum varje månad' }];
  themes = [
    { value: 'DEFAULT-THEME', label: 'Blue' },
    { value: 'LIGHT-THEME', label: 'Light' },
    { value: 'NATURE-THEME', label: 'Nature' },
    { value: 'BLACK-THEME', label: 'Dark' }
  ];

  user: IUser;
  isLoading: boolean = true;
  loadErrorMessage: string = '';
  municipalities: any;
  units: any;
  pensionSavingsCompanies: any;
  
  constructor(
      private router: Router,
      private store: Store<any>,
      private userService: UserService,
      private authenticationService: AuthenticationService,
      private route: ActivatedRoute,
      private municipalityService: MunicipalityService,
      private loadingService: LoadingBarService,
      private bankService: BankService,
      private fileService: FileService,
      private swedishBankAccountValidationService: SwedishBankAccountValidationService,
      public snackBar: MatSnackBar,
      public dialog: MatDialog,
      private changeDetectorRef: ChangeDetectorRef
  ) {

  }
  
  ngOnInit() {
    this.isChildAccount = this.userService.userIsChildAccount();
    this.loadingService.start();
    this.authenticationService.onUserUpdate()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(update => {
        if (update && update.text === 'settings-refresh' && this.router.url.startsWith('/settings')) {
          this.loadUser();
        }
      });
    this.loadUser();
    this.getMunicipalities();
    this.loadPensionSavingsCompanies();
    this.isAdmin = this.userService.userHasRole("admin");
    this.isCompany = this.userService.userHasRole("companyuser");
    this.setTableColumns(this.isAdmin);
    this.getCountries();
    this.getBanks();
  }

  
copyReferralCode(code: string | undefined): void {
  if (!code) return;

  navigator.clipboard.writeText(code).then(() => {
    this.copied = true;

    // Återställ färgen efter 2 sekunder (valfritt)
    setTimeout(() => this.copied = false, 2000);
  }, () => {
    console.error('Det gick inte att kopiera värvningskoden.');
  });
}

  getBanks() {
    this.bankService.getAll()
      .subscribe(response => {
        this.banks = response;
        this.syncResolvedBankFromAccountDetails();
      }, err => alert(err));
  }

  resetModel() {
    this.user.Bank.Id = null; // Reset the ngModel value to null or any default value you want
      }

  bankOnChange(bankId) {
    if (bankId === 2) {
      this.user.Bank = JSON.parse(JSON.stringify(this.banks.find((bank) => bank.Id === bankId)));
      this.user.BankName = this.user.Bank?.Name || '';
      return;
    }

    for (var i = 0; i < this.banks.length; i++) {
      if (this.banks[i].Id == bankId) {
        this.user.Bank = JSON.parse(JSON.stringify(this.banks[i]));
        this.user.BankName = this.user.Bank?.Name || '';
      }
    }

    //Id = i.Bank == null ? 0 : i.Bank.Id,
    //  Name = i.Bank.Name,
    //  AccountNumberTitle = i.Bank.AccountNumberTitle,
    //  AccountNumberValidationErrorText = i.Bank.AccountNumberValidationErrorText,
    //  AccountNumberValidationRegex = i.Bank.AccountNumberValidationRegex,
    //  ClearingNumberTitle = i.Bank.ClearingNumberTitle,
    //  ClearingNumberValidationErrorText = i.Bank.ClearingNumberValidationErrorText,
    //  ClearingNumberValidationRegex = i.Bank.ClearingNumberValidationRegex,
    //  ClearingRequired = i.Bank.ClearingRequired,
    //  IsInternational = i.Bank.IsInternational,

  }

  onBankEntryModeChange(mode: 'swedish' | 'personkonto' | 'international') {
    this.cancelPendingBankValidation();
    this.bankEntryMode = mode;
    this.bankValidationMessage = '';

    if (mode === 'international') {
      const internationalBank = this.banks?.find((bank) => bank.Id === 2);
      if (internationalBank) {
        this.user.Bank = JSON.parse(JSON.stringify(internationalBank));
        this.user.BankName = internationalBank.Name;
      }
      return;
    }

    this.user.Bic = '';
    this.user.Iban = '';

    if (mode === 'personkonto') {
      const nordeaPersonkonto = this.banks?.find((bank) => bank.Id === 5);
      if (nordeaPersonkonto) {
        this.user.Bank = JSON.parse(JSON.stringify(nordeaPersonkonto));
        this.user.BankName = nordeaPersonkonto.Name;
      }
      this.user.ClearingNumber = '';
      this.user.AccountNumber = this.getNordeaPersonkontoAccountNumber();
      this.syncResolvedBankFromAccountDetails();
      return;
    }

    if (this.user.Bank?.Id === 2 || this.user.Bank?.Id === 5) {
      this.user.Bank.Id = 0;
      this.user.Bank.Name = 'Välj bank';
      this.user.Bank.IsInternational = false;
      this.user.BankName = '';
    }

    this.syncResolvedBankFromAccountDetails();
  }

  onClearingNumberChange(value: string) {
    this.user.ClearingNumber = value;
    this.resolveBankFromClearing();
    this.scheduleBankValidation();
  }

  onAccountNumberChange(value: string) {
    if (this.bankEntryMode === 'personkonto') {
      this.user.AccountNumber = this.getNordeaPersonkontoAccountNumber();
      this.scheduleBankValidation();
      return;
    }

    this.user.AccountNumber = value;
    this.scheduleBankValidation();
  }

  private scheduleBankValidation(): void {
    this.cancelPendingBankValidation();
    this.bankValidationMessage = '';
    this.validationErrorMessage = '';
    this.bankValidationTimer = setTimeout(() => {
      this.bankValidationTimer = null;
      this.syncResolvedBankFromAccountDetails();
      this.changeDetectorRef.detectChanges();
    }, 3000);
  }

  private cancelPendingBankValidation(): void {
    if (this.bankValidationTimer != null) {
      clearTimeout(this.bankValidationTimer);
      this.bankValidationTimer = null;
    }
  }

  onAccountNumberFocus() {
    this.syncResolvedBankFromAccountDetails();
  }

  getClearingHint(): string {
    if (this.bankEntryMode !== 'swedish') {
      return '';
    }

    if (this.user?.Bank?.ClearingNumberValidationErrorText) {
      return this.user.Bank.ClearingNumberValidationErrorText;
    }

    return 'Ange 4 eller 5 siffror.';
  }

  getAccountHint(): string {
    if (this.bankEntryMode === 'personkonto') {
      return 'Kontonumret hämtas automatiskt från personnumrets sista 10 siffror.';
    }

    if (this.bankEntryMode === 'international') {
      return '';
    }

    if (this.user?.Bank?.AccountNumberValidationErrorText) {
      return this.user.Bank.AccountNumberValidationErrorText;
    }

    return 'Ange kontonummer.';
  }

  private resolveBankFromClearing(): void {
    if (this.bankEntryMode !== 'swedish' || !this.user || !this.banks?.length) {
      return;
    }

    const resolvedBankFromClearing = this.swedishBankAccountValidationService.resolveBankFromClearing(
      this.banks,
      this.user.ClearingNumber
    );

    this.user.BankName = resolvedBankFromClearing.resolvedBankName || '';

    if (resolvedBankFromClearing.resolvedBank) {
      this.user.Bank = JSON.parse(JSON.stringify(resolvedBankFromClearing.resolvedBank));
      this.user.ClearingNumber = resolvedBankFromClearing.normalizedClearingNumber;
    } else if (this.user.Bank && !this.user.Bank.IsInternational) {
      this.user.Bank.Id = 0;
      this.user.Bank.Name = 'Välj bank';
    }
  }

  private syncResolvedBankFromAccountDetails() {
    if (this.bankValidationTimer != null) {
      return;
    }

    if (!this.user || !this.banks || this.banks.length === 0) {
      return;
    }

    if (this.bankEntryMode === 'international' || this.user.Bank?.IsInternational) {
      this.bankValidationMessage = '';
      return;
    }

    if (this.bankEntryMode === 'personkonto') {
      this.user.ClearingNumber = '';
      this.user.AccountNumber = this.getNordeaPersonkontoAccountNumber();

      if (!this.user.AccountNumber || this.user.AccountNumber === '') {
        this.bankValidationMessage = 'Ange personnummer med 12 siffror för Nordea personkonto.';
        this.user.BankName = 'Nordea personkonto';
        return;
      }

      const result = this.swedishBankAccountValidationService.validate(
        this.banks,
        null,
        this.user.AccountNumber,
        this.user.PersonalNumber
      );

      this.user.BankName = 'Nordea personkonto';
      if (result.resolvedBank) {
        this.user.Bank = JSON.parse(JSON.stringify(result.resolvedBank));
        this.user.AccountNumber = result.normalizedAccountNumber;
      }

      this.bankValidationMessage = result.isValid ? '' : result.message;
      return;
    }

    if ((!this.user.ClearingNumber || this.user.ClearingNumber === '') && (!this.user.AccountNumber || this.user.AccountNumber === '')) {
      this.bankValidationMessage = '';
      this.user.BankName = '';
      return;
    }

    this.resolveBankFromClearing();

    if (!this.user.AccountNumber || this.user.AccountNumber === '') {
      this.bankValidationMessage = '';
      return;
    }

    const result = this.swedishBankAccountValidationService.validate(
      this.banks,
      this.user.ClearingNumber,
      this.user.AccountNumber,
      this.user.PersonalNumber
    );

    if (result.resolvedBank) {
      this.user.Bank = JSON.parse(JSON.stringify(result.resolvedBank));
      this.user.BankName = result.resolvedBankName || this.user.BankName || '';
      this.user.ClearingNumber = result.normalizedClearingNumber;
      this.user.AccountNumber = result.normalizedAccountNumber;
      this.bankValidationMessage = result.isValid ? '' : result.message;
      return;
    }

    this.bankValidationMessage = result.message;
    if (this.user.Bank && !this.user.Bank.IsInternational) {
      this.user.Bank.Id = 0;
      this.user.Bank.Name = 'Välj bank';
    }
  }

  notificationSettingsChange(selectedItems) {

    // Loop all settings
    for (var i = 0; i < this.user.NotificationSettings.length; i++) {
        var setting = this.user.NotificationSettings[i];
      // Loop selected setting
      setting.Active = false;

        for (var e = 0; e < selectedItems.length; e++) {
          var currentSettingId = selectedItems[e];
          if (currentSettingId == setting.Type.Id)
            setting.Active = true; // Selected
        }
    }
  }

  setSelectedNotificationSettings() {

    var selectedNotifications = [];

    // Loop all settings
    for (var i = 0; i < this.user.NotificationSettings.length; i++) {
      var setting = this.user.NotificationSettings[i];

      // Loop selected setting
      if (setting.Active) {
        this.selectedNotificationTypes.push(setting.Type.Id);
      }
    }
  }

  canDeactivate(): boolean {

      if (this.form == null)
      {
          return true;
      }
      
      return this.form.dirty || this.taxadjustmentsEdited
          ? confirm('Vill du avsluta utan att spara?')
          : true;
  }

    setTableColumns(isAdmin: boolean) {
        if (isAdmin) {
            this.displayedColumns = ['type', 'startdate', 'enddate', 'maxamount', 'temptax', 'attachment'];
        }
        else {
          this.displayedColumns = ['type', 'startdate', 'enddate', 'temptax', 'attachment'];
        }
    }

  loadUser()
  {
      const requestId = ++this.loadUserRequestId;
      this.isLoading = true;
      this.loadErrorMessage = '';
      this.loadingService.start();
      this.userService.getCurrentUser()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(value => {
            if (requestId === this.loadUserRequestId) {
              this.userLoaded(value);
            }
          },
          error => {
            if (requestId === this.loadUserRequestId) {
              this.isLoading = false;
              this.loadErrorMessage = 'Kontoinställningarna kunde inte hämtas. Försök igen.';
              this.loadingService.complete();
              this.changeDetectorRef.detectChanges();
            }
            console.log(error);
          }
      );
  }

  getMunicipalities()
  {
      this.municipalityService.getMunicipalities()
          .subscribe(value => this.municipalitiesLoaded(value),
          error => console.log(error)
          );
  }

  getReferrals(userId: string) {
    this.userService.getReferrals(userId)
      .subscribe(response => this.referrals = response,
        error => console.log(error)
      );
  }

  municipalitiesLoaded(municipalities: any) {
    this.municipalities = municipalities
  }

  loadUnits() {
      this.municipalityService.getMunicipalities()
        .subscribe(value => this.municipalitiesLoaded(value),
          error => console.log(error)
        );
  }

  unitsLoaded(units: any) {
    this.units = units;
  }

  loadPensionSavingsCompanies() {
    this.userService.getPensionSavingsCompanies()
      .subscribe(value => this.pensionSavingsCompaniesLoaded(value),
        error => console.log(error)
      );
  }

  pensionSavingsCompaniesLoaded(pensionSavingsCompanies: any) {
    this.pensionSavingsCompanies = pensionSavingsCompanies;
  }

  pensionSavingsCompanyChanged(event: any) {
    
    if (event == 1) {
      this.user.PensionSavingsSettings.PensionSavingsCompany.Id = 1;
      this.user.PensionSavingsSettings.PensionSavingsCompany.TechnicalName = "SPP";
    }    
    else if (event == 2) {
      this.user.PensionSavingsSettings.PensionSavingsCompany.Id = 2;
      this.user.PensionSavingsSettings.PensionSavingsCompany.TechnicalName = "Avanza";
    }    
    else {
      this.user.PensionSavingsSettings.PensionSavingsCompany.Id = 3;
      this.user.PensionSavingsSettings.PensionSavingsCompany.TechnicalName = "NothingSelected";
    }    
  }

  pensionSavingsTypeChanged(event: any) {

    if (event == 1) {
      this.user.PensionSavingsSettings.Unit.Id = 1;
      this.user.PensionSavingsSettings.Unit.TechnicalName = "Percent";
    }
    else {
      this.user.PensionSavingsSettings.Unit.Id = 2;
      this.user.PensionSavingsSettings.Unit.TechnicalName = "Share";
    }
    
  }

  addTaxAdjustment(taxAdjustment: ITaxAdjustment) {

      this.config.data.user = this.user;
        
      let dialogRef = this.dialog.open(EditTaxAdjustmentDialogComponent, this.config);
        
      
      dialogRef.afterClosed().subscribe((result: ITaxAdjustment) => {
            
          if (result) {
            if (this.user.TaxAdjustments == null)
                  this.user.TaxAdjustments = [];
              this.user.TaxAdjustments = [result, ...this.user.TaxAdjustments];
              if (this.dataSource) {
                this.dataSource.data = this.user.TaxAdjustments;
              } else {
                this.dataSource = new MatTableDataSource(this.user.TaxAdjustments);
              }
              this.taxadjustmentsEdited = true;
              this.changeDetectorRef.markForCheck();
          }

          dialogRef = null;
      });

     
  }

  deleteTaxAdjustment(taxAdjustment: any) {
        
      for (var i = 0; i < this.user.TaxAdjustments.length; i++) {
          var d = this.user.TaxAdjustments[i];

          if (d.Guid == taxAdjustment.Guid ) {
              this.user.TaxAdjustments.splice(i, 1);
              this.dataSource = new MatTableDataSource(this.user.TaxAdjustments);
              return;
          }
      }
  }

  changeCoordinationNumberValue(value) { 
    this.user.HasCoordinationNumber = value.checked;

    if (this.user.HasCoordinationNumber) {
      this.coordinationNumberValid = this.validateCoordinationNumber();
      if (!this.coordinationNumberValid) {
        this.coordinationErrorMessage = "Detta är inte ett giltigt samordningsnummer. Om du har ett svenskt personnummer ska du inte kryssa i denna ruta."
      }
      else {
        this.coordinationErrorMessage = "";
      }
    }
    else {
      this.coordinationErrorMessage = "";
    }
  }

  changePrimaryEmployerValue(answer: boolean, event: any) {
    this.user.SampoolenIsPrimaryEmployer = answer;
    this.user.SampoolenIsPrimaryEmployerAnswered = true;
    event.source.checked = true;
  }

  // Ensure full-string regex match (anchor pattern if needed)
  private fullRegexTest(pattern: string, value: string): boolean {
    if (!pattern) { return false; }
    const trimmed = pattern.trim();
    const anchored = (trimmed.startsWith('^') && trimmed.endsWith('$')) ? trimmed : `^(?:${trimmed})$`;
    try {
      return new RegExp(anchored).test(value || '');
    } catch {
      // Malformed backend pattern: fail closed
      return false;
    }
  }

  private bankDetailsAreValid(): boolean {
    if (this.bankEntryMode === 'international') {
      const bic = (this.user.Bic || '').replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();
      const iban = (this.user.Iban || '').replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();

      if (!/^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/.test(bic)) {
        this.validationErrorMessage = 'Du måste ange ett giltigt BIC (8 eller 11 tecken)';
        return false;
      }
      if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) {
        this.validationErrorMessage = 'Du måste ange ett giltigt IBAN (15–34 tecken)';
        return false;
      }
      this.user.Bic = bic;
      this.user.Iban = iban;
      this.bankValidationMessage = '';
      return true;
    }

    if (this.bankEntryMode === 'personkonto') {
      const validationResult = this.swedishBankAccountValidationService.validate(
        this.banks,
        null,
        this.user.AccountNumber,
        this.user.PersonalNumber
      );

      if (!validationResult.isValid) {
        this.bankValidationMessage = validationResult.message;
        this.validationErrorMessage = validationResult.message;
        return false;
      }

      if (validationResult.resolvedBank) {
        this.user.Bank = JSON.parse(JSON.stringify(validationResult.resolvedBank));
        this.user.BankName = validationResult.resolvedBankName;
        this.user.ClearingNumber = '';
        this.user.AccountNumber = validationResult.normalizedAccountNumber;
      }

      this.bankValidationMessage = '';
      return true;
    }

    if (!this.user || !this.user.Bank || !this.user.Bank.Id) {
      if (!this.user?.ClearingNumber && !this.user?.AccountNumber) {
        return true;
      }

      const validationResult = this.swedishBankAccountValidationService.validate(
        this.banks,
        this.user.ClearingNumber,
        this.user.AccountNumber,
        this.user.PersonalNumber
      );

      if (!validationResult.isValid) {
        this.bankValidationMessage = validationResult.message;
        this.validationErrorMessage = validationResult.message;
        return false;
      }

      if (validationResult.resolvedBank) {
        this.user.Bank = JSON.parse(JSON.stringify(validationResult.resolvedBank));
        this.user.BankName = validationResult.resolvedBankName;
        this.user.ClearingNumber = validationResult.normalizedClearingNumber;
        this.user.AccountNumber = validationResult.normalizedAccountNumber;
      }

      this.bankValidationMessage = '';
      return true;
    }

    if (this.user.Bank.IsInternational) {
      const bic = (this.user.Bic || '').replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();
      const iban = (this.user.Iban || '').replace(/[^a-zA-Z0-9]+/g, '').toUpperCase();

      if (!/^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/.test(bic)) {
        this.validationErrorMessage = 'Du måste ange ett giltigt BIC (8 eller 11 tecken)';
        return false;
      }
      if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) {
        this.validationErrorMessage = 'Du måste ange ett giltigt IBAN (15–34 tecken)';
        return false;
      }
      this.user.Bic = bic;
      this.user.Iban = iban;
      return true;
    }

    const validationResult = this.swedishBankAccountValidationService.validate(
      this.banks,
      this.user.ClearingNumber,
      this.user.AccountNumber,
      this.user.PersonalNumber
    );

    if (!validationResult.isValid) {
      this.bankValidationMessage = validationResult.message;
      this.validationErrorMessage = validationResult.message;
      return false;
    }

    if (validationResult.resolvedBank) {
      this.user.Bank = JSON.parse(JSON.stringify(validationResult.resolvedBank));
      this.user.BankName = validationResult.resolvedBankName;
      this.user.ClearingNumber = validationResult.normalizedClearingNumber;
      this.user.AccountNumber = validationResult.normalizedAccountNumber;
    }

    this.bankValidationMessage = '';
    return true;
  }

  validateCoordinationNumber() {
    
    var number = this.user.PersonalNumber.charAt(6);
    
    if (this.user.PersonalNumber.length == 12 && this.user.PersonalNumber.charAt(6) > '3') {
      this.coordinationNumberValid = true;
      return true;
    }
    else {
      this.coordinationNumberValid = false;
      return false;
    }    

  }

  onPayoutTypeChange(newType: number) {
    if (!this.user) {
      return;
    }
    this.user.PayoutType = newType;
    this.ensurePayoutDayConsistency();
  }

  onPayoutDayChange(rawValue: any) {
    if (!this.user) {
      return;
    }
    const parsed = Number(rawValue);
    this.user.PayoutDay = isNaN(parsed) ? 0 : parsed;
  }

  private ensurePayoutDayConsistency() {
    if (!this.user) {
      return;
    }
    if (this.user.PayoutType === 1 || this.user.PayoutType == null) {
      this.user.PayoutDay = 0;
    }
  }

  private payoutDaySelectionIsValid(applyMessage: boolean = false): boolean { 
    if (!this.user) {
      return true;
    }

    if (this.user.PayoutType !== 2) {
      if (applyMessage && this.validationErrorMessage === "Du måste ange en dag i månaden (1-28) för fast utbetalning.") {
        this.validationErrorMessage = "";
      }
      return true;
    }

    const payoutDay = Number(this.user.PayoutDay || 0);
    const isValid = payoutDay >= 1 && payoutDay <= 28;

    if (!isValid) {
      if (applyMessage) {
        this.validationErrorMessage = "Du måste ange en dag i månaden (1-28) för fast utbetalning.";
      }
      return false;
    }

    if (applyMessage && this.validationErrorMessage === "Du måste ange en dag i månaden (1-28) för fast utbetalning.") {
      this.validationErrorMessage = "";
    }
    return true;
  }

  saveValidates() {
    if (!this.user?.Country?.Id) {
      this.validationErrorMessage = "Du måste ange ett land";
      return false;
    }

    if (this.bankValidationTimer != null) {
      return false;
    }

    this.ensurePayoutDayConsistency();

    // Bank field-level validation (regex/length), incl. conditional clearing
    if (!this.bankDetailsAreValid()) {
      return false;
    }

    if (!this.payoutDaySelectionIsValid(true)) {
      return false;
    }

    const pensionSavingsPercent = Number(this.user.PensionSavingsSettings?.Amount);
    if (!Number.isFinite(pensionSavingsPercent) || pensionSavingsPercent < 0 || pensionSavingsPercent > 35) {
      this.validationErrorMessage = "Avsättningen till tjänstepension måste vara mellan 0 och 35 procent av bruttolönen.";
      return false;
    }

    const pensionSavingsCompanyId = Number(this.user.PensionSavingsSettings?.PensionSavingsCompany?.Id);
    if (pensionSavingsPercent > 0 && (!pensionSavingsCompanyId || pensionSavingsCompanyId === 3)) {
      this.validationErrorMessage = "Du måste välja ett tjänstepensionsbolag när avsättningen är större än 0 procent.";
      return false;
    }

    //Email address must be given
    if (this.user.Email == null || this.user.Email == "") {
      this.validationErrorMessage = "Du måste ange en mailadress";
      return false;
    }

    if (this.appId == 1 && (this.user.Bank.Id == null || this.user.Bank.Id == 0)) {
      this.validationErrorMessage = "Du måste ange en bank";
      return false;
    }
        
    if (this.appId == 1 && (this.user.PayoutType == 2 && this.user.PayoutDay == 0)) {
      this.validationErrorMessage = "Du måste ange en dag i månaden (1-28) för fast utbetalning.";
      return false;
    }

    if (this.appId == 1 && this.user.IsCompany) {

      //Country must be set
      if (this.user.Country.Name == null) {
        this.validationErrorMessage = "Du måste ange ett land";
        return false;
      }           

      if (!this.coordinationNumberValid) {
        this.coordinationErrorMessage = "Detta är inte ett giltigt samordningsnummer. Om du har ett svenskt personnummer ska du inte kryssa i denna ruta."
      }
      else {
        this.coordinationErrorMessage = "";
      }

      //If Sweden
      //if (this.user.Country.Name == 'Sverige') {

      //  //Orgnr must be given
      //  if (this.user.OrgNumber == null || this.user.OrgNumber == "") {
      //    this.validationErrorMessage = "Du måste ange ett organisationsnummer";
      //    return false;
      //  }

      //  //Bank account, Bankgiro or Plusgiro must be used
      //  if (((this.user.BankName == null || this.user.BankName == "") || (this.user.AccountNumber == null || this.user.AccountNumber == "") || (this.user.ClearingNumber == null || this.user.ClearingNumber == "")) && (this.user.Bankgiro == null || this.user.Bankgiro == "") && (this.user.Plusgiro == null || this.user.Plusgiro == "")) {
      //    this.validationErrorMessage = "Du måste ange bankuppgifter, Bankgiro eller Plusgiro";
      //    return false;
      //  }
      //}

      //If not Sweden
      if (this.user.Country.Name != null && this.user.Country.Name != 'Sverige') {

        if (this.user.CompanyName == null || this.user.CompanyName == "") {
          this.validationErrorMessage = "Du måste ange ett namn";
          return false;
        }

        if (this.user.Address == null || this.user.Address == "") {
          this.validationErrorMessage = "Du måste ange en gatuadress";
          return false;
        }

        if (this.user.Zip == null || this.user.Zip == "") {
          this.validationErrorMessage = "Du måste ange ett postnummer";
          return false;
        }

        if (this.user.City == null || this.user.City == "") {
          this.validationErrorMessage = "Du måste ange en stad";
          return false;
        }

        if (this.user.Iban == null || this.user.Iban == "") {
          this.validationErrorMessage = "Du måste ange IBAN";
          return false;
        }

        if (this.user.Bic == null || this.user.Bic == "") {
          this.validationErrorMessage = "Du måste ange BIC";
          return false;
        }

        else {
          this.validationErrorMessage = ""
          return true;
        }

      }

      //If not Sweden, but EU country
      if (this.user.Country.Eu && this.user.Country.Name != 'Sverige') {

        //Orgnr must be given
        if (this.user.Vat == null || this.user.Vat == "") {
          this.validationErrorMessage = "Du måste ange VAT";
          return false;
        }
      }

      else {
        this.validationErrorMessage = ""
        return true;
      }

    }

    if (this.appId == 2 || !this.user.IsCompany) {
      if (!this.user.CreatedFromSpar) { 
        if (this.user.FirstName == null || this.user.FirstName == "") {
          this.validationErrorMessage = "Du måste ange förnamn";
          return false;
        }

        if (this.user.LastName == null || this.user.LastName == "") {
          this.validationErrorMessage = "Du måste ange efternamn";
          return false;
        }

        
        if (this.user.Address == null || this.user.Address == "") {
          this.validationErrorMessage = "Du måste ange adress";
          return false;
        }

        if (this.user.Zip == null || this.user.Zip == "") {
          this.validationErrorMessage = "Du måste ange postnummer";
          return false;
        }

        if (this.user.City == null || this.user.City == "") {
          this.validationErrorMessage = "Du måste ange ort";
          return false;
        }

      }

      if (this.user.Phone == null || this.user.Phone == "") {
        this.validationErrorMessage = "Du måste ange telefonnummer";
        return false;
      }


      if (this.user.DisplayName == null || this.user.DisplayName == "") {
        this.validationErrorMessage = "Du måste ange vilket avsändarnamn som ska visas på din faktura";
        return false;
      }

      var regexPn = /[0-9]{12}/g;
      var pnIsValid = regexPn.test(this.user.PersonalNumber);
      
      if (!pnIsValid) {
        this.validationErrorMessage = "Personnumret måste innehålla 12 siffror utan bindestreck";
        return false;
      }
      
      if (pnIsValid && this.user.HasCoordinationNumber == true) {
        this.validateCoordinationNumber();
        if (!this.coordinationNumberValid) {
          this.validationErrorMessage = "Du har angivit ett ogiltigt samordningsnummer. Om du har ett svenskt personnummer ska du inte kryssa i rutan för samordningsnummer."
          return false;
        }
      }

      if (this.user.HasCoordinationNumber == true) {
        var regexCn = /[0-9]{4}/g;
        var cnIsValid = regexCn.test(this.user.BirthYear);
        
        if (!cnIsValid) {
          this.validationErrorMessage = "Du har angivit att du har samordningsnummer, och måste ange ett födelseår med fyra siffror";
          return false;
        }        
      }

      if (this.appId == 1 && (this.user.Bank.Id == null || this.user.Bank.Id == 0)) {
        this.validationErrorMessage = "Du måste ange en bank";
        return false;
      }

      //Bank account, Bankgiro or Plusgiro must be used
      //if ((this.user.BankName == null || this.user.BankName == "") || (this.user.AccountNumber == null || this.user.AccountNumber == "") || (this.user.ClearingNumber == null || this.user.ClearingNumber == "")) {
      //  this.validationErrorMessage = "Du måste ange bankuppgifter";
      //  return false;
      //}            

      else
      {
        this.validationErrorMessage = ""
        return true;
      }

    }
  }

  getCountries() {
    this.userService.getCountries()
      .subscribe(value => this.countries = value,
        error => this.onError(error)
      );
  }

  onError(error: any) {
    alert('An error occured');
    console.log(error);
  }

  onRegisteredAbroadChange(checked: boolean) {
    this.registeredAbroad = checked;
    if (!checked) {
      this.user.Country = { ...this.countries.find(country => country.Id === 1) } as ICountryType;
    }
  }

  onResidenceCountryChange(countryId: number) {
    this.getCountryName(countryId);
    this.registeredAbroad = countryId > 0 && countryId !== 1;
  }

  getCountryName(countryId: number) {
    for (var i = 0; i < this.countries.length; i++) {
      if (this.countries[i].Id == countryId) {

        this.user.Country.Name = this.countries[i].Name;
        this.user.Country.Eu = this.countries[i].Eu;
      }
    }
  }

  userLoaded(user: IUser)
  {    
    this.user = user;
    this.user.Country = this.user.Country || {} as ICountryType;
    this.registeredAbroad = this.user.Country.Id !== 1;
    this.bankEntryMode = this.user?.Bank?.IsInternational ? 'international' : (this.user?.Bank?.Id === 5 ? 'personkonto' : 'swedish');
    if (this.user.TaxAdjustments == null) {
      this.user.TaxAdjustments = [];
    }
    this.user.TaxAdjustments = [...this.user.TaxAdjustments].sort((a, b) => b.Id - a.Id);
    this.dataSource = new MatTableDataSource(this.user.TaxAdjustments);
    this.isLoading = false;
    this.loadingService.complete();
    this.setSelectedNotificationSettings();
    this.CustomServiceFee = user.CustomServiceFee * 100;
    this.Commission = user.Commission * 100;
    this.syncResolvedBankFromAccountDetails();
    
    if (user.PensionSavingsSettings == null) {
      this.setDefaultPensionSavingsSettings();
    }
    else {
      this.pensionSavingsTypeChanged(1);
    }

    this.getReferrals(this.user.Id);
    this.changeDetectorRef.detectChanges();
  }

  onPersonalNumberChange(value: string) {
    this.user.PersonalNumber = value;

    if (this.bankEntryMode === 'personkonto') {
      this.user.AccountNumber = this.getNordeaPersonkontoAccountNumber();
      this.scheduleBankValidation();
    }
  }

  private getNordeaPersonkontoAccountNumber(): string {
    const normalizedPersonalNumber = (this.user?.PersonalNumber || '').replace(/\D+/g, '');
    if (normalizedPersonalNumber.length < 10) {
      return '';
    }

    return normalizedPersonalNumber.slice(-10);
  }

  setDefaultPensionSavingsSettings() { 
    this.pensionSavingsCompanyChanged(1);
    this.pensionSavingsTypeChanged(1);
    this.user.PensionSavingsSettings.Amount = 0;    
  }

  ngOnDestroy(): void {
    this.cancelPendingBankValidation();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSubmit()
  {
    this.cancelPendingBankValidation();
    if (!this.saveValidates()) {
      return;
    }

    this.emailErrorMessage = '';
    this.user.Commission = this.Commission / 100;

    this.isLoading = true;
    this.userService.updatetUser(this.user)
        .subscribe(
        res => this.userUpdated(res),
        err => this.onUserError(err),
            () => console.log('HTTP request completed.')
        );
  }

  selectedAppId() {
    if (this.appId == 1) {
      this.isCompany == false;
    }

    if (this.appId == 2) {
      this.isCompany == true;
    }
  }

  userUpdated(response: any)
  {
      this.isLoading = false;
      this.loadingService.complete();
      this.form?.form.markAsPristine();
      this.taxadjustmentsEdited = false;
      this.router.navigate(['/']);
  }

  onUserError(response: any)
  {
      this.isLoading = false;
      let message = response && response.error
        ? (response.error.ExceptionMessage || response.error.message || response.error)
        : null;
      if (typeof message === 'string') {
        const firstLine = message.split('\n')[0];
        message = firstLine.replace(/^System\.Exception:\s*/i, '').trim();
      }
      if (message === 'PersonalNumberExistsInDb') {
        message = 'Det finns redan ett konto med detta personnummer';
      }
      if (message && message.includes("Epostadressen används redan")) {
        this.emailErrorMessage = message;
        this.showMessage(message);
        return;
      }
      this.showMessage(message);
  }

  showMessage(message: string) {

      let config = new MatSnackBarConfig();
      config.duration = 7000;
      this.snackBar.open(message, undefined, config);
  }

  showFile(file: IFile) {
        this.fileService.openFile(file);
  }

  municipalityChange(municipalityId: any)
  {
    for (var i = 0; i < this.municipalities.length; i++)
    {
      if (this.municipalities[i].Id == municipalityId)
      {
          this.user.Municipality.Tax = this.municipalities[i].Tax;
      }
    }      
  }

  onThemeSelect({ value: theme }) {
    this.store.dispatch(new ActionSettingsChangeTheme({ theme }));
    this.store.dispatch(new ActionSettingsPersist({ settings: this.settings }));
  }

  onAutoNightModeToggle({ checked: autoNightMode }) {
    this.store.dispatch(
      new ActionSettingsChangeAutoNightMode({ autoNightMode })
    );
    this.store.dispatch(new ActionSettingsPersist({ settings: this.settings }));
  }

  onPageAnimationsToggle({ checked: pageAnimations }) {
    this.store.dispatch(
      new ActionSettingsChangeAnimationsPage({ pageAnimations })
    );
    this.store.dispatch(new ActionSettingsPersist({ settings: this.settings }));
  }

  onElementsAnimationsToggle({ checked: elementsAnimations }) {
    this.store.dispatch(
      new ActionSettingsChangeAnimationsElements({ elementsAnimations })
    );
    this.store.dispatch(new ActionSettingsPersist({ settings: this.settings }));
    }

    config = {
        disableClose: false,
        panelClass: 'full-screen-modal',
        hasBackdrop: true,
        backdropClass: '',
        width: '500px',
        maxWidth: '100vw',
        height: '100%',
        position: {
            top: '',
            bottom: '',
            left: '',
            right: ''
        },
        data: {
            invoiceItemGroup: {},
            customerTemplate: {},
            customer: {},
            contact: {},
            companyId: 0,
            status: {},
            user: {} as IUser
        },
        dataTemplate: {
            Id: 0,
            Country: { Id: 1 },
            Type: {},
            HoursWorked: undefined,
            StartDate: undefined,
            EndDate: undefined,
            Comment: undefined,
            InvoiceId: 0
        }
    };

}



